import * as React from "react";
import { ChevronsUpDown, GalleryVerticalEnd, Plus, Phone, MessageSquare } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TopUp } from "@/components/pages/billing/TopUp";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useGetHomeQuery, useGetOrganisationsQuery, useSetActiveOrganisationMutation } from "@/store/rest_api";
import { StatusMenu } from "@/components/pages/dashboard/StatusMenu";
import { useEffect, useState } from "react";
import Organisation from "@/model/interface/Organisation";
import { organisationLogoUrl } from "@/util/orgLogo";
import { apiErrorMessage } from "@/util/apiError";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCallDialog } from "@/context/CallDialogContext";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { useMissedCallsCount } from "@/hooks/useMissedCallsCount";
import { TrialStatusBadge } from "@/components/pages/dashboard/TrialStatusBadge";

export function SidebarOrganisationCard() {
  const { state, toggleSidebar } = useSidebar();
  const { isCallDialogOpen, openCallDialog, requestCloseCallDialog, setSelectedCallerId } = useCallDialog();
  const missedCalls = useMissedCallsCount();
  const { isMessageDialogOpen, openMessageDialog, requestCloseMessageDialog } = useMessageDialog();
  const navigate = useNavigate();
  const [activeOrganisation, setActiveOrganisation] = useState<Organisation | null>(null);
  const { data: userData, isLoading: isLoadingUserData } = useGetHomeQuery();
  const {
    data: organisations,
    isLoading: isLoadingOrganisations,
    refetch: refetchOrganisations,
  } = useGetOrganisationsQuery();
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);

  const [setUserActiveOrganisation] = useSetActiveOrganisationMutation();

  const activeMembership = userData?.activeMembership;
  const canManageBilling =
    activeMembership?.status === MembershipStatus.ACTIVE &&
    (activeMembership.role === MembershipRole.OWNER || activeMembership.role === MembershipRole.ADMIN);

  useEffect(() => {
    if (organisations && organisations.length && userData) {
      const activeOrg = organisations.find((org) => org.id === userData.activeOrganisation?.id);
      if (activeOrg) {
        setActiveOrganisation(activeOrg);
      }
    }
  }, [organisations, userData]);

  if (isLoadingOrganisations && isLoadingUserData) {
    return <>Loading...</>;
  }

  if (!activeOrganisation || !organisations || !organisations?.length) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            onClick={() => navigate("/settings/organisations/new")}
            tooltip={state === "collapsed" ? "Set up an organisation" : undefined}
          >
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Plus className="size-4" />
            </div>
            {state === "expanded" && (
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Set up an organisation</span>
              </div>
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const logoUrl = organisationLogoUrl(activeOrganisation.logoFileId);

  async function updateActiveOrganisation(organisation: Organisation) {
    try {
      await setUserActiveOrganisation(organisation).unwrap();
      setSelectedCallerId("");
      navigate("/dashboard");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not switch organisation"));
    }
  }

  const getTriggerPos = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  const handleCallClick = (e: React.MouseEvent) => {
    if (isCallDialogOpen) {
      requestCloseCallDialog();
    } else {
      requestCloseMessageDialog();
      openCallDialog(getTriggerPos(e));
    }
  };

  const handleMessageClick = (e: React.MouseEvent) => {
    if (isMessageDialogOpen) {
      requestCloseMessageDialog();
    } else {
      requestCloseCallDialog();
      openMessageDialog(getTriggerPos(e));
    }
  };

  const handleTopUpSuccess = () => {
    setIsTopUpOpen(false);
    refetchOrganisations();
  };

  if (state === "collapsed") {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" tooltip={activeOrganisation.name} onClick={toggleSidebar}>
            <div
              className={`flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg ${
                logoUrl ? "border border-sidebar-primary" : "bg-sidebar-primary text-sidebar-primary-foreground"
              }`}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={activeOrganisation.name} className="size-full object-contain" />
              ) : (
                <GalleryVerticalEnd className="size-4" />
              )}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>

        <SidebarMenuItem>
          <StatusMenu isCollapsed />
        </SidebarMenuItem>

        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            tooltip={missedCalls > 0 ? `Calls (${missedCalls} missed)` : "Calls"}
            onClick={handleCallClick}
            data-dialog-launcher
            className="overflow-visible"
          >
            <div className="flex aspect-square size-8 items-center justify-center relative bg-primary rounded-lg">
              <Phone className="size-4 text-primary-foreground" />
              {missedCalls > 0 && (
                <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-destructive ring-2 ring-stone-300"></span>
              )}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <div className="px-2 py-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex w-full items-center gap-3 mb-3 py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
            <div
              className={`flex size-10 items-center justify-center overflow-hidden rounded-lg ${
                logoUrl ? "border border-sidebar-primary" : "bg-sidebar-primary text-sidebar-primary-foreground"
              }`}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={activeOrganisation.name} className="size-full object-contain" />
              ) : (
                <GalleryVerticalEnd className="size-5" />
              )}
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-sm">{activeOrganisation.name}</div>
              <div className="text-xs text-muted-foreground truncate">{activeOrganisation.website}</div>
              <div className="text-xs text-muted-foreground truncate">
                <TrialStatusBadge />
              </div>
            </div>
            <ChevronsUpDown className="size-4 shrink-0 text-primary" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 rounded-lg" align="start" side="right" sideOffset={4}>
          <DropdownMenuLabel className="text-xs text-muted-foreground">Organisations</DropdownMenuLabel>
          {organisations.map((organisation) => (
            <DropdownMenuItem
              key={organisation.name}
              onClick={() => updateActiveOrganisation(organisation)}
              className="gap-2 p-2"
            >
              <div className="flex size-6 items-center justify-center overflow-hidden rounded-sm border">
                {organisationLogoUrl(organisation.logoFileId) ? (
                  <img src={organisationLogoUrl(organisation.logoFileId)} alt={organisation.name} className="size-full object-contain" />
                ) : (
                  <GalleryVerticalEnd className="size-4 shrink-0 text-primary" />
                )}
              </div>
              {organisation.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="gap-2 p-2" onClick={() => navigate("/settings/organisations/new")}>
            <div className="flex size-6 items-center justify-center rounded-sm border">
              <Plus className="size-4 text-primary" />
            </div>
            <div className="font-medium">Add organisation</div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <StatusMenu isCollapsed={false} />

      <div className="flex gap-2 mb-3 px-0">
        <Button variant="outline" size="sm" className="flex-1" onClick={handleMessageClick} data-dialog-launcher>
          <MessageSquare className="size-4 text-primary" />
        </Button>
        <Button variant="outline" size="sm" className="flex-1 relative" onClick={handleCallClick} data-dialog-launcher>
          <Phone className="size-4 text-primary" />
          {missedCalls > 0 && (
            <Badge className="absolute -top-1 -right-1 size-5 flex items-center justify-center p-0 text-xs tabular-nums">
              {missedCalls > 9 ? "9+" : missedCalls}
            </Badge>
          )}
        </Button>
      </div>

      <div className="flex items-center justify-between py-2 px-0 border-t">
        <span className="text-sm font-medium">Balance: ${activeOrganisation.balance?.toFixed(2) || "0.00"}</span>
        {canManageBilling && (
          <Dialog open={isTopUpOpen} onOpenChange={setIsTopUpOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title="Add funds">
                <Plus className="size-4 text-primary" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogTitle>Add Credits</DialogTitle>
              <DialogDescription>Add funds to your account balance</DialogDescription>
              <TopUp onSuccess={handleTopUpSuccess} />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
